import { Button, HStack } from '@chakra-ui/react';
import { Tooltip } from '@components/ui/tooltip';
import { useUpdateProcedureMutation } from '@services/data/procedure.api';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Procedure, ProcedureStepStateEnum } from '@/types/config/procedure.types';

import { useProcedureTabContext } from '../ProcedureTab';

type Props = {
  isMandatory: boolean;
  procedureId: string;
  stepRank: number;
  onValidate: () => Promise<Procedure>;
  isDisabled?: boolean;
  label?: string;
  disabledTooltip?: string;
};

export default function StepActionButtons({
  isMandatory,
  procedureId,
  stepRank,
  onValidate,
  isDisabled = false,
  label = 'notification.procedure.step.validate',
  disabledTooltip,
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { notificationId } = useProcedureTabContext();
  const [updateProcedure] = useUpdateProcedureMutation();
  const [isLoadingCancel, setIsLoadingCancel] = useState<boolean>(false);
  const [isLoadingValidate, setIsLoadingValidate] = useState<boolean>(false);

  return (
    <HStack
      gap={2}
      width="100%"
      alignItems="start"
      backgroundColor="neutral.700"
      paddingY={3}
      flexShrink={0}
      justifyContent="center"
      zIndex={1}
    >
      {!isMandatory && (
        <Button
          loading={isLoadingCancel}
          variant="secondary"
          disabled={isLoadingCancel || isLoadingValidate}
          onClick={() => {
            setIsLoadingCancel(true);
            updateProcedure({ notificationId, procedureId, step: stepRank, state: ProcedureStepStateEnum.IGNORED })
              .unwrap()
              .catch(() => setIsLoadingCancel(false));
          }}
        >
          <FormattedMessage id="notification.procedure.step.skip" />
        </Button>
      )}
      <Tooltip content={disabledTooltip ? formatMessage({ id: disabledTooltip }) : ''} disabled={!isDisabled}>
        <Button
          disabled={isDisabled || isLoadingCancel || isLoadingValidate}
          loading={isLoadingValidate}
          onClick={() => {
            if (isDisabled || isLoadingCancel || isLoadingValidate) {
              return;
            }
            setIsLoadingValidate(true);
            onValidate().catch(() => setIsLoadingValidate(false));
          }}
        >
          <FormattedMessage id={label} />
        </Button>
      </Tooltip>
    </HStack>
  );
}
